import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AdjustmentsHorizontalIcon,
  BellIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
  CheckIcon,
  DocumentTextIcon,
  CalendarIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftIcon,
  InformationCircleIcon,
  UserIcon,
  BellSlashIcon,
  SpeakerWaveIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    // Simulate fetching notification settings
    const fetchSettings = () => {
      const mockSettings = {
        // Global settings
        globalSettings: {
          enabled: true,
          quietHours: {
            enabled: true,
            startTime: '22:00',
            endTime: '07:00'
          },
          frequency: 'immediate', // immediate, hourly, daily, weekly
          maxPerDay: 50,
          groupSimilar: true,
          soundEnabled: true,
          vibrationEnabled: true
        },
        
        // Delivery channels
        channels: {
          inApp: {
            enabled: true,
            name: 'In-App Notifications',
            description: 'Show notifications within the application'
          },
          email: {
            enabled: true,
            name: 'Email Notifications',
            description: 'Send notifications to your email address',
            address: 'user@hospital.com'
          },
          sms: {
            enabled: false,
            name: 'SMS Notifications',
            description: 'Send important notifications via text message',
            phoneNumber: '+1 (555) 123-4567'
          },
          desktop: {
            enabled: true,
            name: 'Desktop Notifications',
            description: 'Show browser/system notifications on desktop'
          }
        },

        // Category-specific settings
        categories: {
          tasks: {
            name: 'Tasks & Assignments',
            description: 'Task assignments, deadlines, and completions',
            icon: ClipboardDocumentCheckIcon,
            settings: {
              inApp: { enabled: true, priority: 'high' },
              email: { enabled: true, priority: 'high' },
              sms: { enabled: false, priority: 'high' },
              desktop: { enabled: true, priority: 'high' }
            }
          },
          documents: {
            name: 'Documents',
            description: 'Document sharing, reviews, and updates',
            icon: DocumentTextIcon,
            settings: {
              inApp: { enabled: true, priority: 'medium' },
              email: { enabled: true, priority: 'medium' },
              sms: { enabled: false, priority: 'low' },
              desktop: { enabled: false, priority: 'medium' }
            }
          },
          events: {
            name: 'Events & Meetings',
            description: 'Meeting reminders, event updates, and calendar changes',
            icon: CalendarIcon,
            settings: {
              inApp: { enabled: true, priority: 'high' },
              email: { enabled: true, priority: 'high' },
              sms: { enabled: false, priority: 'medium' },
              desktop: { enabled: true, priority: 'high' }
            }
          },
          training: {
            name: 'Training & Education',
            description: 'Course assignments, completions, and certifications',
            icon: CheckIcon,
            settings: {
              inApp: { enabled: true, priority: 'medium' },
              email: { enabled: true, priority: 'medium' },
              sms: { enabled: false, priority: 'low' },
              desktop: { enabled: false, priority: 'medium' }
            }
          },
          forms: {
            name: 'Forms & Approvals',
            description: 'Form submissions, approvals, and workflow updates',
            icon: DocumentTextIcon,
            settings: {
              inApp: { enabled: true, priority: 'high' },
              email: { enabled: true, priority: 'high' },
              sms: { enabled: false, priority: 'medium' },
              desktop: { enabled: true, priority: 'high' }
            }
          },
          support: {
            name: 'Support & IT',
            description: 'Support tickets, system maintenance, and IT updates',
            icon: InformationCircleIcon,
            settings: {
              inApp: { enabled: true, priority: 'high' },
              email: { enabled: true, priority: 'high' },
              sms: { enabled: false, priority: 'high' },
              desktop: { enabled: true, priority: 'high' }
            }
          },
          payroll: {
            name: 'Payroll & HR',
            description: 'Payroll information, HR updates, and benefits',
            icon: UserIcon,
            settings: {
              inApp: { enabled: true, priority: 'medium' },
              email: { enabled: true, priority: 'high' },
              sms: { enabled: false, priority: 'low' },
              desktop: { enabled: false, priority: 'medium' }
            }
          },
          forums: {
            name: 'Forums & Discussions',
            description: 'Forum replies, mentions, and discussion updates',
            icon: ChatBubbleLeftIcon,
            settings: {
              inApp: { enabled: true, priority: 'low' },
              email: { enabled: false, priority: 'low' },
              sms: { enabled: false, priority: 'low' },
              desktop: { enabled: false, priority: 'low' }
            }
          }
        },

        // Priority-based settings
        priorities: {
          high: {
            name: 'High Priority',
            description: 'Critical notifications that require immediate attention',
            overrideQuietHours: true,
            forceSound: true,
            persistUntilRead: true
          },
          medium: {
            name: 'Medium Priority',
            description: 'Important notifications that should be addressed soon',
            overrideQuietHours: false,
            forceSound: false,
            persistUntilRead: false
          },
          low: {
            name: 'Low Priority',
            description: 'Informational notifications that can wait',
            overrideQuietHours: false,
            forceSound: false,
            persistUntilRead: false
          }
        }
      };

      setSettings(mockSettings);
      setLoading(false);
    };

    setTimeout(fetchSettings, 500);
  }, []);

  const handleGlobalSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      globalSettings: {
        ...prev.globalSettings,
        [key]: value
      }
    }));
  };

  const handleQuietHoursChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      globalSettings: {
        ...prev.globalSettings,
        quietHours: {
          ...prev.globalSettings.quietHours,
          [key]: value
        }
      }
    }));
  };

  const handleChannelChange = (channel, key, value) => {
    setSettings(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: {
          ...prev.channels[channel],
          [key]: value
        }
      }
    }));
  };

  const handleCategorySettingChange = (category, channel, key, value) => {
    setSettings(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: {
          ...prev.categories[category],
          settings: {
            ...prev.categories[category].settings,
            [channel]: {
              ...prev.categories[category].settings[channel],
              [key]: value
            }
          }
        }
      }
    }));
  };

  const handlePrioritySettingChange = (priority, key, value) => {
    setSettings(prev => ({
      ...prev,
      priorities: {
        ...prev.priorities,
        [priority]: {
          ...prev.priorities[priority],
          [key]: value
        }
      }
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSaving(false);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  const resetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all notification settings to their defaults? This action cannot be undone.')) {
      // Reset logic here
      window.location.reload();
    }
  };

  const toggleAllForCategory = (category, enabled) => {
    const channels = ['inApp', 'email', 'sms', 'desktop'];
    channels.forEach(channel => {
      handleCategorySettingChange(category, channel, 'enabled', enabled);
    });
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'inApp':
        return <BellIcon className="h-5 w-5" />;
      case 'email':
        return <EnvelopeIcon className="h-5 w-5" />;
      case 'sms':
        return <DevicePhoneMobileIcon className="h-5 w-5" />;
      case 'desktop':
        return <ComputerDesktopIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <AdjustmentsHorizontalIcon className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Notification Settings</h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Customize how and when you receive notifications
                  </p>
                </div>
              </div>
              <div className="mt-4 sm:mt-0 flex items-center space-x-3">
                <button
                  onClick={resetToDefaults}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Reset to Defaults
                </button>
                <Link
                  to="/notifications"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <BellIcon className="h-4 w-4 mr-2" />
                  View Notifications
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <CheckIcon className="h-5 w-5 text-green-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Settings saved successfully!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Global Settings */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <BellIconSolid className="h-5 w-5 text-blue-600 mr-2" />
              Global Settings
            </h2>
          </div>
          <div className="p-6 space-y-6">
            {/* Master Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">Enable Notifications</h3>
                <p className="text-sm text-gray-500">
                  Master switch for all notifications
                </p>
              </div>
              <button
                onClick={() => handleGlobalSettingChange('enabled', !settings.globalSettings.enabled)}
                className={`${
                  settings.globalSettings.enabled ? 'bg-blue-600' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                <span
                  className={`${
                    settings.globalSettings.enabled ? 'translate-x-5' : 'translate-x-0'
                  } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                />
              </button>
            </div>

            {/* Notification Frequency */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Frequency
                </label>
                <select
                  value={settings.globalSettings.frequency}
                  onChange={(e) => handleGlobalSettingChange('frequency', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="immediate">Immediate</option>
                  <option value="hourly">Hourly Digest</option>
                  <option value="daily">Daily Digest</option>
                  <option value="weekly">Weekly Digest</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Notifications Per Day
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={settings.globalSettings.maxPerDay}
                  onChange={(e) => handleGlobalSettingChange('maxPerDay', parseInt(e.target.value))}
                  className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Quiet Hours */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 flex items-center">
                    <BellSlashIcon className="h-4 w-4 mr-2" />
                    Quiet Hours
                  </h3>
                  <p className="text-sm text-gray-500">
                    Suppress non-urgent notifications during these hours
                  </p>
                </div>
                <button
                  onClick={() => handleQuietHoursChange('enabled', !settings.globalSettings.quietHours.enabled)}
                  className={`${
                    settings.globalSettings.quietHours.enabled ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      settings.globalSettings.quietHours.enabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              {settings.globalSettings.quietHours.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={settings.globalSettings.quietHours.startTime}
                      onChange={(e) => handleQuietHoursChange('startTime', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={settings.globalSettings.quietHours.endTime}
                      onChange={(e) => handleQuietHoursChange('endTime', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Additional Options */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Group Similar Notifications</h3>
                  <p className="text-sm text-gray-500">
                    Combine similar notifications to reduce clutter
                  </p>
                </div>
                <button
                  onClick={() => handleGlobalSettingChange('groupSimilar', !settings.globalSettings.groupSimilar)}
                  className={`${
                    settings.globalSettings.groupSimilar ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      settings.globalSettings.groupSimilar ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 flex items-center">
                    <SpeakerWaveIcon className="h-4 w-4 mr-2" />
                    Sound Notifications
                  </h3>
                  <p className="text-sm text-gray-500">
                    Play sound when receiving notifications
                  </p>
                </div>
                <button
                  onClick={() => handleGlobalSettingChange('soundEnabled', !settings.globalSettings.soundEnabled)}
                  className={`${
                    settings.globalSettings.soundEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      settings.globalSettings.soundEnabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Vibration (Mobile)</h3>
                  <p className="text-sm text-gray-500">
                    Enable vibration for mobile notifications
                  </p>
                </div>
                <button
                  onClick={() => handleGlobalSettingChange('vibrationEnabled', !settings.globalSettings.vibrationEnabled)}
                  className={`${
                    settings.globalSettings.vibrationEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      settings.globalSettings.vibrationEnabled ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Channels */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Delivery Channels</h2>
            <p className="text-sm text-gray-500 mt-1">
              Configure how you want to receive notifications
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(settings.channels).map(([key, channel]) => (
                <div key={key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="p-2 bg-gray-100 rounded-lg mr-3">
                        {getChannelIcon(key)}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{channel.name}</h3>
                        <p className="text-xs text-gray-500">{channel.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleChannelChange(key, 'enabled', !channel.enabled)}
                      className={`${
                        channel.enabled ? 'bg-blue-600' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          channel.enabled ? 'translate-x-5' : 'translate-x-0'
                        } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </button>
                  </div>

                  {/* Channel-specific settings */}
                  {key === 'email' && channel.enabled && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={channel.address}
                        onChange={(e) => handleChannelChange(key, 'address', e.target.value)}
                        className="block w-full border border-gray-300 rounded-md py-1 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}

                  {key === 'sms' && channel.enabled && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={channel.phoneNumber}
                        onChange={(e) => handleChannelChange(key, 'phoneNumber', e.target.value)}
                        className="block w-full border border-gray-300 rounded-md py-1 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Category Settings */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Notification Categories</h2>
            <p className="text-sm text-gray-500 mt-1">
              Customize notifications for each type of content
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    In-App
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SMS
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Desktop
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(settings.categories).map(([key, category]) => (
                  <tr key={key} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="p-2 bg-gray-100 rounded-lg mr-3">
                          <category.icon className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{category.name}</div>
                          <div className="text-sm text-gray-500">{category.description}</div>
                        </div>
                      </div>
                    </td>
                    {['inApp', 'email', 'sms', 'desktop'].map(channel => (
                      <td key={channel} className="px-6 py-4 text-center">
                        <div className="space-y-2">
                          <button
                            onClick={() => handleCategorySettingChange(key, channel, 'enabled', !category.settings[channel].enabled)}
                            className={`${
                              category.settings[channel].enabled ? 'bg-blue-600' : 'bg-gray-200'
                            } relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                          >
                            <span
                              className={`${
                                category.settings[channel].enabled ? 'translate-x-4' : 'translate-x-0'
                              } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                            />
                          </button>
                          {category.settings[channel].enabled && (
                            <select
                              value={category.settings[channel].priority}
                              onChange={(e) => handleCategorySettingChange(key, channel, 'priority', e.target.value)}
                              className="block w-20 text-xs border border-gray-300 rounded py-1 px-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="low">Low</option>
                              <option value="medium">Med</option>
                              <option value="high">High</option>
                            </select>
                          )}
                        </div>
                      </td>
                    ))}
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => toggleAllForCategory(key, true)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                          title="Enable all"
                        >
                          All On
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => toggleAllForCategory(key, false)}
                          className="text-xs text-red-600 hover:text-red-800"
                          title="Disable all"
                        >
                          All Off
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Priority Settings */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Priority Settings</h2>
            <p className="text-sm text-gray-500 mt-1">
              Configure behavior for different priority levels
            </p>
          </div>
          <div className="p-6 space-y-6">
            {Object.entries(settings.priorities).map(([key, priority]) => (
              <div key={key} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPriorityColor(key)} mr-3`}>
                      {priority.name}
                    </span>
                    <p className="text-sm text-gray-600">{priority.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Override Quiet Hours</span>
                    <button
                      onClick={() => handlePrioritySettingChange(key, 'overrideQuietHours', !priority.overrideQuietHours)}
                      className={`${
                        priority.overrideQuietHours ? 'bg-blue-600' : 'bg-gray-200'
                      } relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          priority.overrideQuietHours ? 'translate-x-4' : 'translate-x-0'
                        } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Force Sound</span>
                    <button
                      onClick={() => handlePrioritySettingChange(key, 'forceSound', !priority.forceSound)}
                      className={`${
                        priority.forceSound ? 'bg-blue-600' : 'bg-gray-200'
                      } relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          priority.forceSound ? 'translate-x-4' : 'translate-x-0'
                        } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Persist Until Read</span>
                    <button
                      onClick={() => handlePrioritySettingChange(key, 'persistUntilRead', !priority.persistUntilRead)}
                      className={`${
                        priority.persistUntilRead ? 'bg-blue-600' : 'bg-gray-200'
                      } relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          priority.persistUntilRead ? 'translate-x-4' : 'translate-x-0'
                        } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
